import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Box, Typography, Alert, CircularProgress, Grid, useTheme, FormControlLabel, Checkbox, Tabs, Tab, Paper, List, ListItem, ListItemIcon, ListItemText, LinearProgress, Chip, Stack } from '@mui/material';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import io from 'socket.io-client';
import supabase from '../supabaseClient'

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview, FilePondPluginFileValidateType);

const UploadRecipe = () => {
    const theme = useTheme();
    const [url, setUrl] = useState('');
    const [files, setFiles] = useState([]);
    const [recipeMessage, setRecipeMessage] = useState(null);
    const [recipeError, setRecipeError] = useState(null);
    const [bookMessage, setBookMessage] = useState(null);
    const [bookError, setBookError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState('');
    const [isBookUploadStarted, setIsBookUploadStarted] = useState(false);
    const statusRef = useRef(null);
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const isDev = process.env.NODE_ENV !== 'production';

    // Dev options (only used in development)
    const [dryRun, setDryRun] = useState(true);
    const [dumpChunks, setDumpChunks] = useState(true);
    const [extractImages, setExtractImages] = useState(true);
    const [localDryRunNoS3, setLocalDryRunNoS3] = useState(true);
    const [maxChunks, setMaxChunks] = useState(5);
    const [maxRecipes, setMaxRecipes] = useState(15);

    const socketRefs = useRef({});
    // Shared socket for “subscribe to all active tasks” after refresh
    const sharedSocketRef = useRef(null);
    const sharedSubscribedTaskIdsRef = useRef(new Set());
    const sharedListenersRef = useRef(new Map()); // taskId -> { updateListener, completeListener, failedListener }
    const [adminEbookTasks, setAdminEbookTasks] = useState({ active: [], completed: [], failed: [] });
    const [adminEbookTasksError, setAdminEbookTasksError] = useState(null);
    const [isAdminEbookTasksLoading, setIsAdminEbookTasksLoading] = useState(false);
    const [adminTab, setAdminTab] = useState('active');
    const [selectedAdminTask, setSelectedAdminTask] = useState(null);
    // Avoid stale closures inside shared socket listeners.
    const selectedAdminTaskRef = useRef(null);

    useEffect(() => {
        selectedAdminTaskRef.current = selectedAdminTask;
    }, [selectedAdminTask]);

    function createSharedSocket(token) {
        const socket = io(SERVER_URL, {
            auth: token ? { token } : undefined,
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
        });

        socket.on('connect', () => {
            // Re-subscribe to all active tasks on reconnect
            sharedSubscribedTaskIdsRef.current.forEach((taskId) => {
                socket.emit('subscribeToTask', { taskId });
            });
        });

        socket.on('connect_error', (err) => {
            console.error('[AdminUploads] shared socket connect_error', err?.message || err);
        });

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                try { socket.connect(); } catch { /* ignore */ }
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        socket.__cleanup = () => {
            document.removeEventListener('visibilitychange', onVisibility);
        };

        return socket;
    }

    function upsertLiveMessageForTask(taskId, message) {
        const msg = typeof message === 'string' ? message : '';
        const patchList = (list) => list.map((t) => (t.id === taskId ? { ...t, _liveMessage: msg } : t));
        setAdminEbookTasks((prev) => ({
            active: patchList(prev.active),
            completed: patchList(prev.completed),
            failed: patchList(prev.failed),
        }));
    }

    function ensureSharedSubscription(taskId) {
        const socket = sharedSocketRef.current;
        if (!socket || !taskId) return;
        if (sharedSubscribedTaskIdsRef.current.has(taskId)) return;

        sharedSubscribedTaskIdsRef.current.add(taskId);
        socket.emit('subscribeToTask', { taskId });

        const updateListener = (data) => {
            if (!data?.message) return;
            upsertLiveMessageForTask(taskId, data.message);
            // IMPORTANT: do not append to the main status panel from the shared socket.
            // The status panel is driven by the per-task socket (created on upload / view details).
            // If we append here too, every message appears twice.
        };

        const completeListener = () => {
            sharedSubscribedTaskIdsRef.current.delete(taskId);
            socket.off(`task-update-${taskId}`, updateListener);
            socket.off(`task-complete-${taskId}`, completeListener);
            socket.off(`task-failed-${taskId}`, failedListener);
            sharedListenersRef.current.delete(taskId);
            fetchAdminEbookTasks();
        };

        const failedListener = (data) => {
            upsertLiveMessageForTask(taskId, data?.error ? `failed: ${data.error}` : 'failed');
            sharedSubscribedTaskIdsRef.current.delete(taskId);
            socket.off(`task-update-${taskId}`, updateListener);
            socket.off(`task-complete-${taskId}`, completeListener);
            socket.off(`task-failed-${taskId}`, failedListener);
            sharedListenersRef.current.delete(taskId);
            fetchAdminEbookTasks();
        };

        socket.on(`task-update-${taskId}`, updateListener);
        socket.on(`task-complete-${taskId}`, completeListener);
        socket.on(`task-failed-${taskId}`, failedListener);
        sharedListenersRef.current.set(taskId, { updateListener, completeListener, failedListener });
    }

    const fetchAdminEbookTasks = async () => {
        setIsAdminEbookTasksLoading(true);
        setAdminEbookTasksError(null);
        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData?.session?.access_token;

            const { data } = await axios.get(
                `${SERVER_URL}/admin/api/tasks/ebooks`,
                {
                    params: { limit: 100 },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const tasks = data?.tasks || [];
            const eff = (t) => (t?.effective_status || t?.status || '').toString().toLowerCase();

            const active = tasks.filter((t) => ['pending', 'queued', 'in_progress', 'active', 'waiting', 'delayed', 'paused'].includes(eff(t)));
            const completed = tasks.filter((t) => eff(t) === 'completed');
            const failed = tasks.filter((t) => ['failed', 'completed_with_warning'].includes(eff(t)));

            setAdminEbookTasks({ active, completed, failed });

            // After refresh, subscribe to ALL active tasks for live updates.
            if (!sharedSocketRef.current) {
                sharedSocketRef.current = createSharedSocket(token);
            }
            active.forEach((t) => ensureSharedSubscription(t.id));
        } catch (e) {
            setAdminEbookTasksError(e?.response?.data?.error || e?.message || 'Failed to load admin ebook tasks');
        } finally {
            setIsAdminEbookTasksLoading(false);
        }
    };

    const isTaskActive = (status) => !['completed', 'failed', 'completed_with_warning'].includes((status || '').toLowerCase());

    const TaskRow = ({ task }) => {
        const statusLower = (task.effective_status || task.status || '').toLowerCase();
        const Icon = statusLower === 'failed'
            ? ErrorIcon
            : statusLower === 'completed'
                ? CheckCircle
                : HourglassEmpty;

        const filename = task?.metadata?.filename || task?.metadata?.source_key || task.id;
        const titles = task?.result?.uploadedRecipeTitles || [];
        const liveMsg = task?._liveMessage;

        return (
            <Paper
                elevation={0}
                sx={{
                    mb: 1.5,
                    p: 1.5,
                    border: `1px solid ${theme.palette.border?.main || 'rgba(255,255,255,0.12)'}`,
                    cursor: 'pointer',
                }}
                onClick={() => setSelectedAdminTask(task)}
            >
                <ListItem sx={{ alignItems: 'flex-start', p: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                        <Icon color={statusLower === 'failed' ? 'error' : statusLower === 'completed' ? 'success' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Typography sx={{ fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace", fontSize: '0.9rem' }} noWrap>
                                {filename}
                            </Typography>
                        }
                        secondary={
                            <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                {task.id} • {(task.effective_status || task.status)} • {task.updated_at ? new Date(task.updated_at).toLocaleString() : ''}
                            </Typography>
                        }
                    />
                </ListItem>

                {isTaskActive(task.status) && <LinearProgress sx={{ mt: 1 }} />}

                {typeof liveMsg === 'string' && liveMsg.trim().length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }} noWrap>
                        last: {liveMsg}
                    </Typography>
                )}

                {Array.isArray(titles) && titles.length > 0 && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {titles.slice(0, 8).map((t, idx) => (
                            <Chip key={`${task.id}-t-${idx}`} label={t} size="small" sx={{ maxWidth: 220 }} />
                        ))}
                        {titles.length > 8 && <Chip label={`+${titles.length - 8} more`} size="small" />}
                    </Stack>
                )}
            </Paper>
        );
    };

    const createTaskSocket = (taskId, token, { onUpdate, onComplete, onFailed, onConnectError } = {}) => {
        const socket = io(SERVER_URL, {
            auth: token ? { token } : undefined,
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
        });

        socket.on('connect', () => {
            socket.emit('subscribeToTask', { taskId });
        });

        socket.on(`task-update-${taskId}`, (data) => {
            if (typeof onUpdate === 'function') onUpdate(data);
        });

        socket.on(`task-complete-${taskId}`, (data) => {
            if (typeof onComplete === 'function') onComplete(data);
        });

        socket.on(`task-failed-${taskId}`, (data) => {
            if (typeof onFailed === 'function') onFailed(data);
        });

        socket.on('connect_error', (err) => {
            if (typeof onConnectError === 'function') onConnectError(err);
        });

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                try { socket.connect(); } catch { /* ignore */ }
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        socket.on('disconnect', () => {
            // keep; reconnection enabled
        });

        // Store hook for cleanup
        socket.__cleanup = () => {
            document.removeEventListener('visibilitychange', onVisibility);
        };

        return socket;
    };

    useEffect(() => {
      // Snapshot ref values for cleanup (CRA/CI treats the lint warning as an error).
      const socketsToCleanUp = socketRefs.current;
      const shared = sharedSocketRef.current;
      const listenersSnapshot = sharedListenersRef.current;
      const subscribedTaskIdsSnapshot = sharedSubscribedTaskIdsRef.current;

      return () => {
        // Cleanup sockets on unmount
        Object.values(socketsToCleanUp).forEach((socket) => {
          try {
            socket.__cleanup?.();
          } catch {
            /* ignore */
          }
          socket.disconnect();
        });

        // Cleanup shared socket + listeners
        try {
          if (shared) {
            listenersSnapshot.forEach((handlers, taskId) => {
              shared.off(`task-update-${taskId}`, handlers.updateListener);
              shared.off(`task-complete-${taskId}`, handlers.completeListener);
              shared.off(`task-failed-${taskId}`, handlers.failedListener);
            });
            listenersSnapshot.clear();
            subscribedTaskIdsSnapshot.clear();
            try {
              shared.__cleanup?.();
            } catch {
              /* ignore */
            }
            shared.disconnect();
          }
          sharedSocketRef.current = null;
        } catch {
          /* ignore */
        }
      };
    }, []);

    useEffect(() => {
        if (statusRef.current) {
            statusRef.current.scrollTop = statusRef.current.scrollHeight;
        }
    }, [status]);

    useEffect(() => {
        // Load recent ebook tasks on mount so user can resume after refresh/close.
        fetchAdminEbookTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setRecipeMessage(null);
        setRecipeError(null);
        setIsLoading(true);

        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData.session.access_token;
    
            // Send the request to the server
            const response = await axios.post(
                `${SERVER_URL}/api/uploads/scrape-url`,
                { url }, // Data to be sent in the body
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log(response.data)

            const { task_id } = response.data;
            console.log('Task ID:', task_id);

            // Now set up a socket listener for this task_id
            const socket = io(SERVER_URL);

            socket.on('connect', () => {
                console.log('Connected to server');
                
                // Subscribe to task updates
                socket.emit('subscribeToTask', { taskId: task_id });

                // Listen for task updates using new event format
                socket.on(`task-update-${task_id}`, (data) => {
                    console.log('Task Update:', data);
                    if (data.message) {
                        setStatus(prevStatus => `${prevStatus.trim()}\n${data.message}`);
                    }
                });

                socket.on(`task-complete-${task_id}`, (data) => {
                    console.log('Task Completed:', data);
                    setRecipeMessage('Recipe processing completed successfully.');
                    setRecipeError(null);
                    setIsLoading(false);
                    socket.disconnect();
                });

                socket.on(`task-failed-${task_id}`, (data) => {
                    console.error('Task Failed:', data);
                    setRecipeError(data.error || 'Recipe processing failed due to an error.');
                    setRecipeMessage(null);
                    setIsLoading(false);
                    socket.disconnect();
                });
            });

            // Store socket reference to disconnect later if needed
            socketRefs.current[task_id] = socket;

        } catch (err) {
            console.error('Error submitting URL:', err);
            setRecipeError(err.response?.data?.error || 'An error occurred');
            setIsLoading(false);
        }
    };

    const handleFileUpload = async (fieldName, file, metadata, load, error, progress, abort) => {
        setIsBookUploadStarted(true);
        setIsLoading(true);
        // Always show *something* immediately so local dev doesn't feel "stuck" during upload.
        setStatus((prevStatus) => `${(prevStatus || '').trim()}\nUploading file…`.trim());

        try {
            console.log('Starting upload for:', file.name);
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData.session.access_token;
            const effectiveFileType = file.type || (file.name?.toLowerCase().endsWith('.epub') ? 'application/epub+zip' : 'application/octet-stream');

            // Dev optimization: allow local dry-runs without S3 multipart upload
            if (isDev && dryRun && localDryRunNoS3) {
                const formData = new FormData();
                formData.append('file', file, file.name);
                formData.append('devOptions', JSON.stringify({
                    dryRun,
                    dumpChunks,
                    extractImages,
                    dumpResults: true,
                    maxChunks: Number.isFinite(maxChunks) ? maxChunks : null,
                    maxRecipes: Number.isFinite(maxRecipes) ? maxRecipes : null,
                }));

                const { data: completionData } = await axios.post(
                    `${SERVER_URL}/api/uploads/process-local-ebook`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            progress(true, percentCompleted);
                        },
                    }
                );

                const { task_id } = completionData;
                load(file.name);
                setBookMessage('File uploaded to server (local dry-run). Processing started.');
                setStatus((prevStatus) => `${(prevStatus || '').trim()}\nQueued task: ${task_id}`.trim());

                setSelectedAdminTask({ id: task_id, metadata: { filename: file.name }, status: 'in_progress' });
                // Same "no live status yet" fallback used for S3 flow, but for local dry-run too.
                let receivedAnyEvent = false;
                const fallbackTimer = setTimeout(() => {
                    if (receivedAnyEvent) return;
                    // If we don't see any socket events quickly, check the job state.
                    // This avoids confusion when the job is simply queued behind another ebook job (concurrency=1).
                    (async () => {
                        try {
                            const { data: sessionData } = await supabase.auth.getSession();
                            const token2 = sessionData?.session?.access_token;
                            const { data } = await axios.get(
                                `${SERVER_URL}/admin/api/queues/ebook/${task_id}/state`,
                                { headers: { Authorization: `Bearer ${token2}` } }
                            );
                            const st = (data?.state || '').toString();
                            if (st) {
                                setStatus(prevStatus => `${prevStatus.trim()}\nQueued (state=${st}). Waiting for worker…`);
                                return;
                            }
                        } catch {
                            // ignore; fall back to generic message below
                        }
                        setStatus(prevStatus =>
                            `${prevStatus.trim()}\nProcessing started (no live status updates yet).`
                        );
                    })();
                }, 10000);
                const socket = createTaskSocket(task_id, token, {
                    onUpdate: (data) => {
                        receivedAnyEvent = true;
                        if (data?.message) setStatus(prevStatus => `${prevStatus.trim()}\n${data.message}`);
                    },
                    onComplete: () => {
                        clearTimeout(fallbackTimer);
                        setBookMessage('Ebook processing completed successfully.');
                        setBookError(null);
                        setIsLoading(false);
                        setIsBookUploadStarted(false);
                        try { socket.__cleanup?.(); } catch { /* ignore */ }
                        socket.disconnect();
                    },
                    onFailed: (data) => {
                        clearTimeout(fallbackTimer);
                        setBookError(data?.error || 'Ebook processing failed due to an error.');
                        setBookMessage(null);
                        setIsLoading(false);
                        setIsBookUploadStarted(false);
                        try { socket.__cleanup?.(); } catch { /* ignore */ }
                        socket.disconnect();
                    },
                    onConnectError: (err) => {
                        console.error('Local ebook socket connect error:', err);
                        clearTimeout(fallbackTimer);
                        setStatus(prevStatus =>
                            `${prevStatus.trim()}\nLocal processing started (live status connection failed).`
                        );
                    }
                });
                socketRefs.current[task_id] = socket;
                return;
            }

            const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            let start = 0;
            let parts = [];

            const { data: { uploadId } } = await axios.post(`${SERVER_URL}/api/uploads/start-upload`, {
                filename: file.name,
                fileType: effectiveFileType,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log('Upload ID:', uploadId);

            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                console.log('Requesting presigned URL for chunk:', chunkIndex);
                const { data: { presignedUrl, partNumber } } = await axios.post(`${SERVER_URL}/api/uploads/get-presigned-url`, {
                    uploadId,
                    chunkIndex,
                    filename: file.name,
                    fileType: effectiveFileType,
                }, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                console.log('Presigned URL:', presignedUrl);

                const response = await axios.put(presignedUrl, chunk, {
                    headers: {
                        'Content-Type': effectiveFileType,
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        progress(true, (chunkIndex * 100 / totalChunks) + (percentCompleted / totalChunks));
                    },
                });

                parts.push({ ETag: response.headers.etag, PartNumber: partNumber });
                start = end;
            }

            console.log('Completing upload with parts:', parts);
            const { data: completionData } = await axios.post(`${SERVER_URL}/api/uploads/complete-upload`, {
                uploadId,
                filename: file.name,
                parts,
                // `devOptions` naming is legacy; we also use it for prod-safe toggles like extractImages.
                devOptions: {
                    extractImages,
                    ...(isDev ? {
                        dryRun,
                        dumpChunks,
                        dumpResults: true,
                        maxChunks: Number.isFinite(maxChunks) ? maxChunks : null,
                        maxRecipes: Number.isFinite(maxRecipes) ? maxRecipes : null,
                    } : {}),
                },
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const { task_id } = completionData;
            console.log('Task ID:', task_id);

            load(file.name);
            setBookMessage('File uploaded successfully to S3. Processing started.');

            // Live status: reconnect-safe socket that re-subscribes on reconnect
            let receivedAnyEvent = false;
            const fallbackTimer = setTimeout(() => {
                if (receivedAnyEvent) return;
                setStatus(prevStatus =>
                    `${prevStatus.trim()}\nProcessing started (no live status updates yet).`
                );
            }, 10000);

            setSelectedAdminTask({ id: task_id, metadata: { filename: file.name }, status: 'in_progress' });
            const socket = createTaskSocket(task_id, token, {
                onUpdate: (data) => {
                    receivedAnyEvent = true;
                    if (data?.message) {
                        setStatus(prevStatus => `${prevStatus.trim()}\n${data.message}`);
                    }
                },
                onComplete: () => {
                    clearTimeout(fallbackTimer);
                    setBookMessage('Ebook processing completed successfully.');
                    setBookError(null);
                    setIsLoading(false);
                    setIsBookUploadStarted(false);
                    try { socket.__cleanup?.(); } catch { /* ignore */ }
                    socket.disconnect();
                    fetchAdminEbookTasks();
                },
                onFailed: (data) => {
                    clearTimeout(fallbackTimer);
                    setBookError(data?.error || 'Ebook processing failed due to an error.');
                    setBookMessage(null);
                    setIsLoading(false);
                    setIsBookUploadStarted(false);
                    try { socket.__cleanup?.(); } catch { /* ignore */ }
                    socket.disconnect();
                    fetchAdminEbookTasks();
                },
                onConnectError: (err) => {
                    console.error('Ebook processor socket connect error:', err);
                    clearTimeout(fallbackTimer);
                    setStatus(prevStatus =>
                        `${prevStatus.trim()}\nProcessing started (live status connection failed; you can resume via the task dropdown).`
                    );
                    // Keep the socket around (reconnect enabled); don't disconnect.
                }
            });

            socketRefs.current[task_id] = socket;
            fetchAdminEbookTasks();

        } catch (err) {
            console.error('Error uploading file:', err);
            error(err.response?.data?.error || err.message || 'Upload failed');
            setBookError(err.response?.data?.error || 'An error occurred');
            setIsLoading(false);
            setIsBookUploadStarted(false);
            abort();
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
                {/* Upload Recipe Form */}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        upload recipe
                    </Typography>
                    <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="url"
                        label="recipe URL"
                        name="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        sx={{ mt: 2 }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                    {recipeMessage && <Alert severity="success" sx={{ mt: 2 }}>{recipeMessage}</Alert>}
                    {recipeError && <Alert severity="error" sx={{ mt: 2 }}>{recipeError}</Alert>}
                </Box>

                {/* Upload Book Section */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        upload book
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={extractImages} onChange={(e) => setExtractImages(e.target.checked)} />}
                            label="extract images (prod: uploads + sets image_url; dev dry-run: writes local previews)"
                        />
                    </Box>
                    <Paper sx={{ mb: 2, p: 2 }} elevation={0}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h6">admin uploads</Typography>
                            <Button size="small" variant="outlined" onClick={fetchAdminEbookTasks} disabled={isAdminEbookTasksLoading}>
                                {isAdminEbookTasksLoading ? 'refreshing…' : 'refresh'}
                            </Button>
                        </Box>
                        {adminEbookTasksError && <Alert severity="error" sx={{ mt: 1 }}>{adminEbookTasksError}</Alert>}

                        <Tabs
                            value={adminTab}
                            onChange={(_e, v) => setAdminTab(v)}
                            sx={{ mt: 1 }}
                        >
                            <Tab label={`Active (${adminEbookTasks.active.length})`} value="active" />
                            <Tab label={`Completed (${adminEbookTasks.completed.length})`} value="completed" />
                            <Tab label={`Failed (${adminEbookTasks.failed.length})`} value="failed" />
                        </Tabs>

                        <Box sx={{ mt: 2 }}>
                            {adminTab === 'active' && (
                                <List sx={{ p: 0 }}>
                                    {(adminEbookTasks.active || []).slice(0, 20).map((t) => (
                                        <TaskRow key={t.id} task={t} />
                                    ))}
                                </List>
                            )}
                            {adminTab === 'completed' && (
                                <List sx={{ p: 0 }}>
                                    {(adminEbookTasks.completed || []).slice(0, 20).map((t) => (
                                        <TaskRow key={t.id} task={t} />
                                    ))}
                                </List>
                            )}
                            {adminTab === 'failed' && (
                                <List sx={{ p: 0 }}>
                                    {(adminEbookTasks.failed || []).slice(0, 20).map((t) => (
                                        <TaskRow key={t.id} task={t} />
                                    ))}
                                </List>
                            )}
                        </Box>

                        {selectedAdminTask && (
                            <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    selected: {selectedAdminTask.metadata?.filename || selectedAdminTask.id}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={async () => {
                                            try {
                                                setStatus('');
                                                const { data: sessionData } = await supabase.auth.getSession();
                                                const token = sessionData?.session?.access_token;
                                                const { data: task } = await axios.get(
                                                    `${SERVER_URL}/admin/api/tasks/${selectedAdminTask.id}`,
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                );
                                                const logs = Array.isArray(task?.logs) ? task.logs.join('\n') : '';
                                                const headerParts = [];
                                                if (task?.effective_status || task?.status) headerParts.push(`status: ${task?.effective_status || task?.status}`);
                                                if (task?.queue_state) headerParts.push(`queue: ${task.queue_state}`);
                                                const progressMsg =
                                                    typeof task?.queue_progress?.status === 'string'
                                                        ? task.queue_progress.status
                                                        : typeof task?.queue_progress?.message === 'string'
                                                            ? task.queue_progress.message
                                                            : null;
                                                if (progressMsg) headerParts.push(`progress: ${progressMsg}`);
                                                const header = headerParts.length ? headerParts.join(' • ') : '';
                                                const titles = Array.isArray(task?.result?.uploadedRecipeTitles)
                                                    ? `\n\ntitles:\n- ${task.result.uploadedRecipeTitles.slice(0, 50).join('\n- ')}`
                                                    : '';
                                                setStatus([header, logs].filter(Boolean).join('\n') + titles);
                                                // ensure live updates (reconnect-safe)
                                                const existing = socketRefs.current[selectedAdminTask.id];
                                                if (!existing) {
                                                    const liveSocket = createTaskSocket(selectedAdminTask.id, token, {
                                                        onUpdate: (data) => {
                                                            if (data?.message) setStatus(prev => `${prev.trim()}\n${data.message}`);
                                                        },
                                                        onComplete: () => fetchAdminEbookTasks(),
                                                        onFailed: () => fetchAdminEbookTasks(),
                                                    });
                                                    socketRefs.current[selectedAdminTask.id] = liveSocket;
                                                }
                                            } catch (e) {
                                                setBookError(e?.response?.data?.error || e?.message || 'Failed to load task');
                                            }
                                        }}
                                    >
                                        view details
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={async () => {
                                            try {
                                                const { data: sessionData } = await supabase.auth.getSession();
                                                const token = sessionData?.session?.access_token;
                                                await axios.post(
                                                    `${SERVER_URL}/admin/api/queues/ebook/${selectedAdminTask.id}/cancel`,
                                                    {},
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                );
                                                setStatus(prev => `${prev.trim()}\ncancel requested`);
                                                fetchAdminEbookTasks();
                                            } catch (e) {
                                                setBookError(e?.response?.data?.error || e?.message || 'Failed to cancel job');
                                            }
                                        }}
                                    >
                                        cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                    {isDev && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
                                dev options (dry-run dumps chunks + extracted recipes to disk; no db writes)
                            </Typography>
                            <FormControlLabel
                                control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
                                label="dry run (no supabase writes)"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={dumpChunks} onChange={(e) => setDumpChunks(e.target.checked)} />}
                                label="dump chunks.json"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={localDryRunNoS3} onChange={(e) => setLocalDryRunNoS3(e.target.checked)} />}
                                label="local dry-run (skip S3 upload)"
                            />
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <TextField
                                    label="max chunks"
                                    type="number"
                                    size="small"
                                    value={maxChunks}
                                    onChange={(e) => setMaxChunks(parseInt(e.target.value || '0', 10))}
                                />
                                <TextField
                                    label="max recipes"
                                    type="number"
                                    size="small"
                                    value={maxRecipes}
                                    onChange={(e) => setMaxRecipes(parseInt(e.target.value || '0', 10))}
                                />
                            </Box>
                        </Box>
                    )}
                    <div style={{ display: 'none' }}>
                        <style>
                            {`
                                .filepond--credits {
                                    display: none !important;
                                }
                            `}
                        </style>
                    </div>
                    <FilePond
                        files={files}
                        onupdatefiles={setFiles}
                        allowMultiple={true}
                        server={{
                            process: handleFileUpload,
                        }}
                        name="files" /* sets the file input name, it's filepond by default */
                        labelIdle="drag 'n drop files or <span class='filepond--label-action'>browse</span>"
                        acceptedFileTypes={['application/epub+zip', 'application/octet-stream']}

                                            />
                    {bookMessage && <Alert severity="success" sx={{ mt: 2 }}>{bookMessage}</Alert>}
                    {bookError && <Alert severity="error" sx={{ mt: 2 }}>{bookError}</Alert>}
                </Box>

                {/* Status Messages (upload flow + resumed task details) */}
                {(isBookUploadStarted || (selectedAdminTask && status.trim())) && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            status
                        </Typography>
                        <Box
                            ref={statusRef}
                            sx={{
                                p: 2,
                                maxHeight: 200,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                backgroundColor: theme.palette.background.paper,
                                border: '1px solid #ddd',
                                borderRadius: 2,
                                color: theme.palette.text.primary
                            }}
                        >
                            {status.trim()}
                        </Box>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default UploadRecipe;